<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import type { IdeaWithVotes, DotPosition } from '../types';
import ConfirmDialog from './ConfirmDialog.vue';
import HandHelping from './icons/HandHelping.vue';
import AlertCircle from './icons/AlertCircle.vue';
import Heart from './icons/Heart.vue';
import Trash2 from './icons/Trash2.vue';
import Edit from './icons/Edit.vue';
import MessageSquare from './icons/MessageSquare.vue';

interface Props {
  idea: IdeaWithVotes;
  groupedDots?: boolean;
  isMobile?: boolean;
  visibleVoteTypes?: ('liked' | 'important' | 'supported')[];
  currentUserId?: string;
  userRole?: 'owner' | 'member' | 'reader' | null;
  votesLocked?: boolean;
  commentsEnabled?: boolean;
  isDetailOpen?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  groupedDots: false,
  isMobile: false,
  visibleVoteTypes: () => ['liked', 'important', 'supported'],
  votesLocked: false,
  commentsEnabled: true,
  isDetailOpen: false,
});

// Determine if user can edit/delete this idea
const canEdit = computed(() => {
  if (props.userRole === 'owner') return true;
  if (props.userRole === 'member') {
    return props.idea.createdBy === props.currentUserId;
  }
  return false;
});

const emit = defineEmits({
  delete: (_id: string) => true,
  vote: (
    _id: string,
    _type: 'liked' | 'important' | 'supported',
    _onError: (() => void) | undefined
  ) => true,
  edit: (_id: string) => true,
  tagClick: (_tag: string) => true,
  comment: (_id: string) => true,
  click: () => true,
});

const showControls = ref(false);
const isElevated = ref(false);
const showDeleteConfirm = ref(false);
let elevationTimeout: number | null = null;
let controlsTimeout: number | null = null;

function handleMouseEnter() {
  // Clear any pending timeouts
  if (elevationTimeout) {
    clearTimeout(elevationTimeout);
    elevationTimeout = null;
  }
  if (controlsTimeout) {
    clearTimeout(controlsTimeout);
    controlsTimeout = null;
  }
  // Elevate first
  isElevated.value = true;
  // Always show controls when entering the card
  showControls.value = true;
}

function handleMouseLeave() {
  // Delay hiding controls to allow moving into helper zone
  controlsTimeout = window.setTimeout(() => {
    showControls.value = false;
  }, 50);
  // Keep elevated during the closing animation (200ms)
  elevationTimeout = window.setTimeout(() => {
    isElevated.value = false;
  }, 200);
}

function handleHelperZoneEnter() {
  // Clear the timeout that would hide controls
  if (controlsTimeout) {
    clearTimeout(controlsTimeout);
    controlsTimeout = null;
  }
  // Also clear elevation timeout
  if (elevationTimeout) {
    clearTimeout(elevationTimeout);
    elevationTimeout = null;
  }
  showControls.value = true;
  isElevated.value = true;
}

function handleHelperZoneLeave(event: MouseEvent) {
  // Check if we're moving back to the parent card
  const relatedTarget = event.relatedTarget as HTMLElement;
  const sidebarWrapper = event.currentTarget as HTMLElement;
  const cardElement = sidebarWrapper.parentElement as HTMLElement;

  // If moving back to the card (the card is the parent of the sidebar), don't close
  if (
    relatedTarget &&
    cardElement &&
    (cardElement === relatedTarget || cardElement.contains(relatedTarget))
  ) {
    return;
  }

  showControls.value = false;
  // Keep elevated during closing animation
  elevationTimeout = window.setTimeout(() => {
    isElevated.value = false;
  }, 200);
}

// Track toggle states from Convex userVotes
const voteStates = computed(() => ({
  liked: optimisticVotes.value.liked ?? props.idea.userVotes?.liked ?? false,
  important: optimisticVotes.value.important ?? props.idea.userVotes?.important ?? false,
  supported: optimisticVotes.value.supported ?? props.idea.userVotes?.supported ?? false,
}));

// Optimistic vote counts
const optimisticVoteCounts = ref<{
  liked: number | null;
  important: number | null;
  supported: number | null;
}>({ liked: null, important: null, supported: null });

// Optimistic vote states (overrides props)
const optimisticVotes = ref<{
  liked: boolean | null;
  important: boolean | null;
  supported: boolean | null;
}>({ liked: null, important: null, supported: null });

// Computed vote counts that use optimistic values when available
const voteCounts = computed(() => ({
  liked: optimisticVoteCounts.value.liked ?? props.idea.liked,
  important: optimisticVoteCounts.value.important ?? props.idea.important,
  supported: optimisticVoteCounts.value.supported ?? props.idea.supported,
}));

// Reset optimistic state when idea changes
watch(
  () => props.idea.id,
  () => {
    optimisticVoteCounts.value = { liked: null, important: null, supported: null };
    optimisticVotes.value = { liked: null, important: null, supported: null };
  }
);

const totalScore = computed(() => {
  return props.visibleVoteTypes.reduce((sum, type) => sum + (voteCounts.value[type] || 0), 0);
});

// Cache for seeded random values to avoid recomputation
const randomCache = new Map<string, number>();

// Generate stable random values based on idea ID (seeded random)
function seededRandom(seed: string): number {
  // Check cache first
  if (randomCache.has(seed)) {
    return randomCache.get(seed)!;
  }

  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  const result = Math.abs(hash) / 2147483647;

  // Cache the result
  randomCache.set(seed, result);
  return result;
}

// Generate random tape properties for each card (stable based on idea ID)
// Using lazy initialization to only compute once per card
const tapeRandom = computed(() => {
  const seed = props.idea.id;
  return {
    offsetX: (seededRandom(seed + 'tapeX') - 0.5) * 20, // -10% to +10%
    offsetY: seededRandom(seed + 'tapeY') * 8, // 0 to 8px
    rotation: (seededRandom(seed + 'tapeRot') - 0.5) * 8, // -4deg to +4deg
    width: seededRandom(seed + 'tapeW') * 5, // 0% to 5% (makes width 15-20%)
    height: seededRandom(seed + 'tapeH') * 8, // 0 to 8px (makes height 20-28px)
  };
});

const isVoteTypeVisible = (type: 'liked' | 'important' | 'supported') => {
  return props.visibleVoteTypes.includes(type);
};

// Determine effective grouped dots mode - auto-activate if any category exceeds 12 votes
const effectiveGroupedDots = computed(() => {
  // If globally enabled, use grouped mode
  if (props.groupedDots) return true;

  // Otherwise, check if any single category has too many votes for scattered display
  const maxScatteredDots = 12;
  return props.visibleVoteTypes.some(type => (voteCounts.value[type] || 0) > maxScatteredDots);
});

const colorClasses = {
  yellow: 'bg-(--color-postit-yellow)',
  pink: 'bg-(--color-postit-pink)',
  blue: 'bg-(--color-postit-blue)',
  green: 'bg-(--color-postit-green)',
  orange: 'bg-[var(--color-postit-orange)]',
};

function generateDotPositions(
  count: number,
  type: 'liked' | 'important' | 'supported',
  existingPositions: DotPosition[] = []
): DotPosition[] {
  const positions: DotPosition[] = [...existingPositions];
  const maxAttempts = 50;

  for (let i = 0; i < count; i++) {
    let attempts = 0;
    let position: DotPosition | null = null;

    while (attempts < maxAttempts) {
      const x = 55 + Math.random() * 35;
      const y = 60 + Math.random() * 30;

      const tooClose = positions.some(p => Math.abs(p.x - x) < 8 && Math.abs(p.y - y) < 10);

      if (!tooClose) {
        position = { x, y, type };
        break;
      }
      attempts++;
    }

    if (position) {
      positions.push(position);
    }
  }

  // Only return the new positions (not the existing ones we started with)
  return positions.slice(existingPositions.length);
}

// Store dot positions persistently so they don't jump around when votes change
const likedDotPositions = ref<DotPosition[]>([]);
const importantDotPositions = ref<DotPosition[]>([]);
const supportedDotPositions = ref<DotPosition[]>([]);

// Only add/remove dots, never regenerate existing ones
function updateDotPositions(
  currentPositions: DotPosition[],
  newCount: number,
  type: 'liked' | 'important' | 'supported'
): DotPosition[] {
  const maxDots = 12;
  const targetCount = Math.min(newCount, maxDots);

  // If we need more dots, generate new ones
  if (targetCount > currentPositions.length) {
    const additionalNeeded = targetCount - currentPositions.length;
    const newPositions = generateDotPositions(additionalNeeded, type, currentPositions);
    return [...currentPositions, ...newPositions];
  }

  // If we need fewer dots, remove from the end
  if (targetCount < currentPositions.length) {
    return currentPositions.slice(0, targetCount);
  }

  // Count hasn't changed, keep positions as-is
  return currentPositions;
}

// Watch vote counts and only add/remove dots, don't regenerate
watch(
  () => props.idea.liked,
  newCount => {
    likedDotPositions.value = updateDotPositions(likedDotPositions.value, newCount, 'liked');
  },
  { immediate: true }
);

watch(
  () => props.idea.important,
  newCount => {
    importantDotPositions.value = updateDotPositions(
      importantDotPositions.value,
      newCount,
      'important'
    );
  },
  { immediate: true }
);

watch(
  () => props.idea.supported,
  newCount => {
    supportedDotPositions.value = updateDotPositions(
      supportedDotPositions.value,
      newCount,
      'supported'
    );
  },
  { immediate: true }
);

const allDots = computed(() => [
  ...likedDotPositions.value,
  ...importantDotPositions.value,
  ...supportedDotPositions.value,
]);

function toggleVote(type: 'liked' | 'important' | 'supported') {
  // Check if votes are locked
  if (props.votesLocked) return;

  // Optimistic update: immediately update UI
  const currentHasVote = optimisticVotes.value[type] ?? props.idea.userVotes?.[type] ?? false;
  const currentCount = optimisticVoteCounts.value[type] ?? props.idea[type] ?? 0;

  // Toggle the vote state
  optimisticVotes.value[type] = !currentHasVote;
  // Update the count (add if voting, subtract if unvoting)
  const newCount = currentHasVote ? currentCount - 1 : currentCount + 1;
  optimisticVoteCounts.value[type] = newCount;

  // Optimistically update dot positions
  const dotPositionsMap = {
    liked: likedDotPositions,
    important: importantDotPositions,
    supported: supportedDotPositions,
  };
  const dotPositions = dotPositionsMap[type];
  const currentDots = dotPositions.value;
  const maxDots = 12;
  const targetCount = Math.min(newCount, maxDots);

  let newDotPositions: DotPosition[];
  if (targetCount > currentDots.length) {
    // Adding dots - generate new ones optimistically
    const additionalNeeded = targetCount - currentDots.length;
    const newPositions = generateDotPositions(additionalNeeded, type, currentDots);
    newDotPositions = [...currentDots, ...newPositions];
    dotPositions.value = newDotPositions;
  } else if (targetCount < currentDots.length) {
    // Removing dots - remove from the end
    newDotPositions = currentDots.slice(0, targetCount);
    dotPositions.value = newDotPositions;
  }

  // Emit to parent with error callback for revert
  emit('vote', props.idea.id, type, () => {
    // Revert on error
    optimisticVotes.value[type] = currentHasVote;
    optimisticVoteCounts.value[type] = currentCount;
    // Revert dot positions
    dotPositions.value = currentDots;
  });
}

function handleDelete() {
  showDeleteConfirm.value = true;
}

function confirmDelete() {
  showDeleteConfirm.value = false;
  emit('delete', props.idea.id);
}

function cancelDelete() {
  showDeleteConfirm.value = false;
}

function handleEdit() {
  emit('edit', props.idea.id);
}

function handleTagClick(tag: string) {
  emit('tagClick', tag);
}

function handleCardClick() {
  // Don't open detail panel if user is selecting text
  const selection = window.getSelection();
  if (selection && selection.toString().length > 0) {
    return;
  }
  emit('click');
}
</script>

<template>
  <div
    :class="['postit relative p-3 rounded-sm min-h-35 cursor-pointer', colorClasses[idea.color]]"
    :style="{ transform: `rotate(${idea.rotation}deg)`, zIndex: isElevated ? 50 : 1 }"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @click="handleCardClick"
  >
    <!-- Paper texture overlay -->
    <div class="paper-texture rounded-sm"></div>

    <!-- Tape strip with random properties -->
    <div
      class="tape-strip"
      :style="{
        '--tape-offset-x': `${tapeRandom.offsetX}%`,
        '--tape-offset-y': `${tapeRandom.offsetY}px`,
        '--tape-rotation': `${tapeRandom.rotation}deg`,
        '--tape-width': `${tapeRandom.width}%`,
        '--tape-height': `${tapeRandom.height}px`,
      }"
    ></div>

    <!-- Total score badge -->
    <div class="absolute top-2 right-2 bg-black/10 rounded-full px-2 py-1 text-sm font-bold">
      {{ totalScore }}
    </div>

    <!-- Comment count badge (beneath total score) -->
    <div
      v-if="(idea.commentCount || 0) > 0"
      class="absolute top-10 right-2 bg-white/60 rounded-full px-1.5 py-0.5 text-xs font-semibold flex items-center gap-1 cursor-pointer hover:bg-white/80 transition-colors"
      @click.stop="$emit('comment', idea.id)"
      :title="$t('comments.view')"
    >
      <MessageSquare class="w-3 h-3 text-gray-600" />
      <span class="text-gray-700">{{ idea.commentCount }}</span>
    </div>

    <!-- Content -->
    <div class="relative z-10">
      <h3 class="font-bold text-lg mb-1 pr-8 cursor-text">{{ idea.title }}</h3>
      <p v-if="idea.person" class="text-xs opacity-70 mb-2 cursor-text">
        {{ $t('idea.by') }} {{ idea.person }}
      </p>
      <p v-if="idea.explanation" class="text-sm opacity-80 line-clamp-3 mb-3 cursor-text">
        {{ idea.explanation }}
      </p>

      <!-- Tags - stacked vertically -->
      <div class="flex flex-col gap-1 mb-2">
        <span
          v-for="tag in idea.tags"
          :key="tag"
          @click.stop="handleTagClick(tag)"
          class="text-xs px-2 py-0.5 bg-black/10 rounded-full cursor-pointer hover:bg-black/20 transition-colors w-fit capitalize"
        >
          {{ tag }}
        </span>
      </div>
    </div>

    <!-- Scattered dots -->
    <template v-if="!effectiveGroupedDots">
      <div
        v-for="(dot, index) in allDots"
        :key="`${dot.type}-${index}`"
        v-show="isVoteTypeVisible(dot.type)"
        class="dot absolute"
        :class="{
          'dot-liked': dot.type === 'liked',
          'dot-important': dot.type === 'important',
          'dot-supported': dot.type === 'supported',
        }"
        :style="{ left: `${dot.x}%`, top: `${dot.y}%` }"
      ></div>
    </template>

    <!-- Grouped dots - positioned at bottom-right with background -->
    <div v-if="effectiveGroupedDots" class="absolute bottom-0 right-0 z-10">
      <div class="bg-white/40 rounded-tl-lg px-2 py-1 flex gap-1.5">
        <div v-if="isVoteTypeVisible('liked')" class="flex items-center gap-0.5">
          <Heart class="w-3 h-3 text-(--color-dot-liked)" />
          <span class="text-xs font-bold">{{ voteCounts.liked }}</span>
        </div>
        <div v-if="isVoteTypeVisible('important')" class="flex items-center gap-0.5">
          <AlertCircle
            class="w-3 h-3 text-(--color-dot-important) drop-shadow-[0_0_0.5px_rgba(0,0,0,1)]"
          />
          <span class="text-xs font-bold">{{ voteCounts.important }}</span>
        </div>
        <div v-if="isVoteTypeVisible('supported')" class="flex items-center gap-0.5">
          <HandHelping class="w-3 h-3 text-(--color-dot-supported)" />
          <span class="text-xs font-bold">{{ voteCounts.supported }}</span>
        </div>
      </div>
    </div>

    <!-- Desktop hover controls - floating side menu with helper zone -->
    <div
      v-if="!isMobile && !isDetailOpen"
      class="absolute -top-4 -right-12 p-2"
      style="z-index: 10"
      @mouseenter="handleHelperZoneEnter"
      @mouseleave="handleHelperZoneLeave"
    >
      <div
        :class="[
          'bg-white rounded-lg shadow-lg p-1.5 flex flex-col gap-1 transition-all duration-200',
          showControls && !isDetailOpen
            ? 'opacity-100 visible translate-x-0'
            : 'opacity-0 invisible translate-x-2',
        ]"
        @click.stop
      >
        <!-- Vote buttons - toggleable -->
        <button
          @click.stop="toggleVote('liked')"
          :disabled="votesLocked"
          :class="[
            'p-1.5 rounded-md transition-all duration-200 flex items-center justify-center',
            votesLocked
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-50'
              : voteStates.liked
                ? 'bg-red-100 text-(--color-dot-liked) cursor-pointer'
                : 'bg-gray-100 text-gray-400 hover:bg-gray-200 cursor-pointer',
          ]"
          :title="votesLocked ? $t('workshop.votingLocked') : 'Toggle Like'"
        >
          <Heart class="w-4 h-4" />
        </button>
        <button
          @click.stop="toggleVote('important')"
          :disabled="votesLocked"
          :class="[
            'p-1.5 rounded-md transition-all duration-200 flex items-center justify-center',
            votesLocked
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-50'
              : voteStates.important
                ? 'bg-amber-100 text-(--color-dot-important) cursor-pointer'
                : 'bg-gray-100 text-gray-400 hover:bg-gray-200 cursor-pointer',
          ]"
          :title="votesLocked ? $t('workshop.votingLocked') : 'Toggle Important'"
        >
          <AlertCircle class="w-4 h-4" />
        </button>
        <button
          @click.stop="toggleVote('supported')"
          :disabled="votesLocked"
          :class="[
            'p-1.5 rounded-md transition-all duration-200 flex items-center justify-center',
            votesLocked
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-50'
              : voteStates.supported
                ? 'bg-blue-100 text-(--color-dot-supported) cursor-pointer'
                : 'bg-gray-100 text-gray-400 hover:bg-gray-200 cursor-pointer',
          ]"
          :title="votesLocked ? $t('workshop.votingLocked') : 'Toggle Support'"
        >
          <HandHelping class="w-4 h-4" />
        </button>

        <!-- Divider -->
        <div class="h-px bg-gray-200 my-0.5"></div>

        <!-- Comment button -->
        <button
          v-if="commentsEnabled"
          @click.stop="$emit('comment', idea.id)"
          class="p-1.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all duration-200 flex items-center justify-center cursor-pointer"
          :title="$t('comments.add')"
        >
          <MessageSquare class="w-4 h-4" />
        </button>

        <!-- Divider - only show if user can edit -->
        <div v-if="canEdit" class="h-px bg-gray-200 my-0.5"></div>

        <!-- Edit button - only show if user can edit -->
        <button
          v-if="canEdit"
          @click.stop="handleEdit"
          class="p-1.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all duration-200 flex items-center justify-center cursor-pointer"
          title="Edit Idea"
        >
          <Edit class="w-4 h-4" />
        </button>

        <!-- Delete button - only show if user can edit -->
        <button
          v-if="canEdit"
          @click.stop="handleDelete"
          class="p-1.5 rounded-md bg-gray-100 text-(--color-destructive) hover:bg-red-100 transition-all duration-200 flex items-center justify-center cursor-pointer"
          title="Delete Idea"
        >
          <Trash2 class="w-4 h-4" />
        </button>
      </div>
    </div>

    <!-- Delete confirmation dialog -->
    <ConfirmDialog
      v-model="showDeleteConfirm"
      :title="$t('confirm.deleteIdeaTitle')"
      :message="$t('confirm.deleteIdea')"
      :confirm-text="$t('common.delete')"
      :cancel-text="$t('common.cancel')"
      confirm-color="destructive"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
  </div>
</template>
